import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Wallet, CheckCircle, AlertCircle } from 'lucide-react';
import { useGetPublicMembershipPlansQuery, usePurchaseMembershipMutation, useGetProfileQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import Navbar from '../../components/common/Navbar';

const MembershipPurchase: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAuthenticated = !!user;
  const planIdNum = planId ? parseInt(planId) : null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { data: plansResponse } = useGetPublicMembershipPlansQuery();
  const plan = plansResponse?.data?.find((p) => p.plan_id === planIdNum);
  const { data: profileResponse } = useGetProfileQuery({ role: user?.role || 'USER' });
  const [purchaseMembership] = usePurchaseMembershipMutation();

  const profile = profileResponse?.data;
  const walletBalance = parseFloat(profile?.wallet_balance || '0');
  const planPrice = plan ? (typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price) : 0;
  const monthlyAmount = plan?.payment_type === 'monthly_installment' && plan.monthly_installment_amount
    ? (typeof plan.monthly_installment_amount === 'string' ? parseFloat(plan.monthly_installment_amount) : plan.monthly_installment_amount)
    : 0;
  const paymentAmount = plan?.payment_type === 'monthly_installment' ? monthlyAmount : planPrice;

  useEffect(() => {
    if (!isAuthenticated) {
      if (window.innerWidth < 1024) {
        window.location.href = 'https://app.diasporawise.com';
      } else {
        navigate('/');
      }
      return;
    }
  }, [isAuthenticated, navigate]);

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!plan) {
      toast.error('Plan not found');
      return;
    }

    // Check wallet balance
    if (walletBalance < paymentAmount) {
      toast.error('Insufficient wallet balance. Please add funds to your wallet.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await purchaseMembership({
        plan_id: planIdNum!,
        payment_type: plan.payment_type, // Use plan's payment_type (one_time or monthly_installment)
      }).unwrap();

      if (result?.success) {
        setShowSuccessModal(true);
      } else {
        toast.error(result?.message || 'Failed to purchase membership');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to purchase membership. Please try again.');
      console.error('Membership purchase error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!plan) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-6 sm:py-8 px-4">
          <div className="max-w-4xl mx-auto">
          <Card className="p-4 sm:p-6 md:p-8 text-center">
            <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Plan Not Found</h2>
            <Button onClick={() => navigate('/membership/plans')}>Back to Plans</Button>
          </Card>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-6 sm:py-8 px-4">
        <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/membership/plans')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 transition-colors text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Back to Plans</span>
        </button>

        <Card className="p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Purchase Membership</h1>

          {/* Plan Summary */}
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{plan.name}</h2>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div>
                <span className="text-sm sm:text-base text-gray-600">Total Amount</span>
                {plan.payment_type === 'monthly_installment' && monthlyAmount > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {plan.monthly_installment_count} payments of {formatCurrency(monthlyAmount)} each
                  </p>
                )}
              </div>
              <span className="text-xl sm:text-2xl font-bold text-gray-900">
                {formatCurrency(paymentAmount)}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                <span>{plan.duration_days} days access</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mt-1">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                <span>Up to {plan.max_bookings_per_month} bookings per month</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">Payment via Wallet</h3>
                <p className="text-xs sm:text-sm text-gray-700 mb-2">
                  Payment will be deducted from your wallet balance automatically.
                </p>
                <div className="text-xs sm:text-sm">
                  <span className="text-gray-600">Available Balance: </span>
                  <span className="font-semibold text-gray-900">{formatCurrency(walletBalance)}</span>
                </div>
                {walletBalance < paymentAmount && (
                  <p className="text-xs sm:text-sm text-red-600 mt-2">
                    Insufficient balance. Please add funds to your wallet.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/membership/plans')}
                className="flex-1 w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || walletBalance < paymentAmount}
                className="flex-1 w-full sm:w-auto bg-gray-800 hover:bg-gray-900"
              >
                {isSubmitting ? 'Processing...' : 'Complete Purchase'}
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/membership/status');
        }}
        title="Membership Purchased Successfully!"
      >
        <div className="text-center py-4 px-2">
          <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4" />
          <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
            Your membership has been activated successfully! You can now book apartments.
          </p>
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/apartments');
            }}
            className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900"
          >
            Browse Apartments
          </Button>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default MembershipPurchase;
